






























































































































































































































































































































import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { get } from 'vuex-pathify';

import mixins from '@/utils/mixins';
import Validatable from '@/mixins/validatable';
import BindsAttrs from '@/mixins/binds-attrs';

import {
  QuoteFreightTemplate,
  IQuoteFreightTemplate,
  QuoteFreightItem,
  IQuoteFreightItemData,
  IQuoteFreightItem
} from '@/models';
import { ErrorHandler } from '@/models/errors';
import { omit } from 'lodash';

import { ErrorManager } from '@/models/error';
import QuillPDFEditor from '../common/QuillPDFEditor.vue';
import QuoteTemplateNoteDropDownItem from './section/QuoteTemplateNoteDropDownItem.vue';
import QuoteTemplateNoteTooltipIcon from './templates/common/QuoteTemplateNoteTooltipIcon.vue';
import QuoteFreightsEstimatesFormDialog from './QuoteFreightsEstimatesFormDialog.vue';
import QuoteFreightSectionEstimatesDisplay from './section/QuoteFreightSectionEstimatesDisplay.vue';

const baseMixins = mixins(BindsAttrs, Validatable);

interface Options extends InstanceType<typeof baseMixins> {
  /* eslint-disable-next-line camelcase */
  $_modelEvent: string;
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'QuotesFreightItemsSection',

    components: {
      QuillPDFEditor,
      QuoteTemplateNoteDropDownItem,
      QuoteTemplateNoteTooltipIcon,
      QuoteFreightsEstimatesFormDialog,
      QuoteFreightSectionEstimatesDisplay
    },

    props: {
      editable: Boolean,
      exchangeRate: Number,
      currencyCode: String,

      language: {
        type: String
      },

      errors: {
        type: Object as PropType<ErrorHandler>
      }
    },

    data: () => ({
      selectedFreight: null as null | number,
      loading: false,
      errorMessage: ''
    }),

    watch: {
      internalValue: {
        handler() {
          this.validate();
        },
        deep: true
      }
    },

    mounted() {
      this.fetchFreights();
    },

    computed: {
      ...mapGetters({
        getQuoteFreightTemplateById: 'quoteTemplate/getQuoteFreightTemplateById'
      }),

      quoteFreightTemplates: get<IQuoteFreightTemplate[]>(
        'quoteTemplate/getQuoteFreightTemplates'
      ),

      requiredRule() {
        return (field: string) => [
          (v: string) => !!v || `${field} is required`
        ];
      }
    },

    beforeCreate() {
      // v-radio-group needs to emit a different event
      // https://github.com/vuetifyjs/vuetify/issues/4752
      this.$_modelEvent =
        (this.$options.model && this.$options.model.event) || 'input';
    },

    methods: {
      ...mapActions({
        fetchQuoteFreightTemplates: 'quoteTemplate/fetchQuoteFreightTemplates'
      }),

      clearAllErrors() {
        this.errorMessage = '';
      },

      fetchFreights() {
        this.clearAllErrors();
        this.loading = true;
        return this.fetchQuoteFreightTemplates()
          .catch(error => {
            this.errorMessage = ErrorManager.extractApiError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      addFreightItem() {
        this.clearAllErrors();

        this.internalValue.push(new QuoteFreightTemplate());
      },

      removeFreightItem(item: IQuoteFreightTemplate, index?: number) {
        this.clearAllErrors();

        const itemIndex = index ?? this.internalValue.indexOf(item);
        this.internalValue.splice(itemIndex, 1);
      },

      setFreight(value: IQuoteFreightTemplate, index: number) {
        this.clearAllErrors();

        const freightItemData: IQuoteFreightItemData = {
          template: value['id']!,
          ...omit(value, ['id']),
          price: 0
        };
        //https://vuejs.org/v2/guide/reactivity.html#For-Arrays.
        this.$set(
          this.internalValue,
          index,
          new QuoteFreightItem(freightItemData)
        );

        this.selectedFreight = null;
      },

      clearSelectedValue(index: number) {
        this.internalValue[index].id = null;
        this.internalValue[index].template = null;
        this.internalValue[index].title = '';
        this.internalValue[index].description = '';
        this.internalValue[index].estimates = null;
      },

      textChanged(index: number) {
        // clear template ID whenever text changes
        // to prevent a translation lookup for custom text
        this.internalValue[index].template = null;
      },

      freightItemTemplateNote(freightItem: IQuoteFreightItem) {
        const template = this.getQuoteFreightTemplateById(freightItem.template);
        let note = '';
        if (template) {
          note = template.note;
        }
        return note;
      }
    }
  });
